import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SESSION_DURATION = 15 * 60 * 1000;
const WARNING_BEFORE = 2 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const sessionStartRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const performLogout = useCallback(async () => {
    clearTimers();
    setShowSessionWarning(false);
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  }, [clearTimers]);

  const startSessionTimers = useCallback(() => {
    clearTimers();
    if (!user) return;
    sessionStartRef.current = Date.now();

    warningTimerRef.current = setTimeout(() => {
      setShowSessionWarning(true);
      const warningMs = WARNING_BEFORE;
      setRemainingSeconds(Math.floor(warningMs / 1000));
      countdownRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_DURATION - WARNING_BEFORE);

    logoutTimerRef.current = setTimeout(() => {
      performLogout();
    }, SESSION_DURATION);
  }, [user, clearTimers, performLogout]);

  const extendSession = useCallback(async () => {
    setShowSessionWarning(false);
    clearTimers();
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      startSessionTimers();
    } catch {
      performLogout();
    }
  }, [clearTimers, startSessionTimers, performLogout]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      startSessionTimers();
    } else {
      clearTimers();
      setShowSessionWarning(false);
    }
    return () => clearTimers();
  }, [user, startSessionTimers, clearTimers]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, preferred_language = 'ja') => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      { email, password, name, preferred_language },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await performLogout();
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch (error) {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshToken, checkAuth, showSessionWarning, remainingSeconds, extendSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
